import { Component } from '@angular/core'

interface ChangelogSection {
    title: string
    items: string[]
}

interface ChangelogEntry {
    version: string
    date: string
    summary: string
    sections: ChangelogSection[]
}

export const CURRENT_VERSION = '1.1'

export const CHANGELOG: ChangelogEntry[] = [
    {
        version: '1.1',
        date: '2025-08-05',
        summary: 'Google Ads offline conversion tracking, prospecting improvements, map search enhancements, and merchant report cleanup.',
        sections: [
            {
                title: 'Marketing',
                items: [
                    'Google Ads offline conversion tracking with automated hourly upload of subscription activations',
                    'Campaign tracking now captures gclid, gbraid, and wbraid click identifiers with Safari ITP fallback via localStorage',
                    'Standard utm_source and utm_campaign parameters now map to existing attribution fields',
                    'Admin page to view and monitor ad conversion upload status',
                ],
            },
            {
                title: 'Prospecting',
                items: [
                    'Prospect type classification (Landlord / Tenant) with filtering across board, table, and map views',
                    'Search term filter added to all prospecting views',
                    'Prospecting map now shows nearest parkings when clicking a closed lead',
                    'Map markers now filter to visible bounds for better performance',
                ],
            },
            {
                title: 'Public Website',
                items: [
                    'Map search shows a pin at the searched location and displays distance to each parking result',
                    'Search results sorted by proximity when a location search is active',
                    'Parking bays no longer block booking when count is zero — availability managed operationally',
                ],
            },
            {
                title: 'Admin Dashboard & Operations',
                items: [
                    'Expiring contracts email report expanded to a 7-day lookahead with recently-expired section, renewal status, revenue-at-risk summary, and assigned user',
                    'Changelog page added to admin section for tracking platform version history',
                ],
            },
            {
                title: 'Merchant Portal',
                items: [
                    'Merchant PDF report simplified — removed bay count and occupancy columns, added monthly revenue per site',
                ],
            },
        ],
    },
    {
        version: '1.0',
        date: '2025-08-04',
        summary: 'Initial platform release capturing the full ParkUpp parking subscription system.',
        sections: [
            {
                title: 'Public Website',
                items: [
                    'Home page with parking search, testimonials, and how-it-works section',
                    'Map-based parking search with geolocation, clustering, and marker interaction',
                    'Individual parking listing pages with image galleries, pricing, and booking flow',
                    'About Us, FAQs, Contact, Privacy, Terms & Conditions, and Data Deletion pages',
                    'Corporate landing page for business parking enquiries',
                    'Blog system with create, list, and detail pages',
                    'SEO meta tags, Google Analytics, Tag Manager, Facebook Pixel, and LinkedIn tracking',
                    'Responsive mobile-first design across all public pages',
                    'QR code sharing for parking listings',
                ],
            },
            {
                title: 'Customer App',
                items: [
                    'Email/password and magic link sign-in with forgot password flow',
                    'Client profile management with image upload and phone number',
                    'Parker and vehicle registration required for booking',
                    'Parking subscription request and booking flow',
                    'Credit card payment via PayGate with 3D Secure',
                    'PayFast subscription payment support',
                    'Client dashboard with active subscriptions, invoices, and payment history',
                    'Payment method management (add, view, remove credit cards)',
                    'Subscription cancellation and renewal flows',
                    'Automated email and WhatsApp notifications for bookings, payments, and renewals',
                ],
            },
            {
                title: 'Merchant Portal',
                items: [
                    'Merchant profile creation and management with branding settings',
                    'Multi-member merchant accounts with role-based access',
                    'Member invite system with email-based acceptance flow',
                    'Parking listing creation with map location, amenities, images, terms, and pricing',
                    'Support for Fixed Term, Month-to-Month, and Allocated parking types',
                    'Parking layout visual editor for bay allocation',
                    'Bank details management for payout collection',
                    'Merchant dashboard with subscription overview and financial summaries',
                    'Statement generation and tenant/occupant tracking',
                    'BookUpp white-label tenant booking portal',
                ],
            },
            {
                title: 'Admin CRM',
                items: [
                    'Kanban-style lead management with status pipeline and assignment',
                    'Lead detail view with comments, interaction history, and WhatsApp integration',
                    'Leads report with daily email distribution',
                    'Campaign tracking with UTM source attribution',
                    'CRM export and contact list management',
                ],
            },
            {
                title: 'Billing & Invoicing',
                items: [
                    'Automated monthly invoice generation for active subscriptions',
                    'Manual invoice creation by admin',
                    'Invoice status lifecycle (Draft, Sent, Paid, Overdue, Cancelled, Void)',
                    'PayGate token-based recurring payment collection',
                    'PayFast subscription payment integration',
                    'Daily automated payment collection cron',
                    'Payment bounce handling and retry logic',
                    'VAT calculation and reporting',
                    'Payout batch creation, approval, and settlement workflow',
                    'Invoice aging reports and financial reconciliation tools',
                ],
            },
            {
                title: 'Access Control',
                items: [
                    'Admyt access control integration with center and tenant management',
                    'Tenant and user provisioning via Admyt API',
                    'Vehicle registration sync to Admyt for gate access',
                    'Admin audit pages for Admyt centers, tenants, users, and API logs',
                ],
            },
            {
                title: 'Admin Dashboard & Operations',
                items: [
                    'Executive dashboard with key metrics (total bays, active subs, revenue, occupancy)',
                    'Monthly financial dashboard with charts and trend analysis',
                    'Marketing health dashboard with search analytics and conversion tracking',
                    'Admin parking map for portfolio visualisation',
                    'Expiring leases tracker and subscription data sync',
                    'Known Issues audit tracking system',
                    'Admin user management with master-key login capability',
                    'Data hygiene tools and migration utilities',
                ],
            },
            {
                title: 'Notifications & Communications',
                items: [
                    'Transactional email system via SendGrid with branded templates',
                    'WhatsApp notifications via WATI for bookings, payments, and renewals',
                    'Slack integration for system alerts and exception notifications',
                    'Omnisend marketing automation integration',
                    'Email and WhatsApp audit logging',
                ],
            },
            {
                title: 'Enterprise & White-Label',
                items: [
                    'Enterprise account management with custom branding',
                    'BookUpp white-label subdomain support for merchant booking portals',
                    'Redefine Properties integration and custom homepage',
                    'Enterprise API with token-based authentication',
                ],
            },
            {
                title: 'Prospecting',
                items: [
                    'Map-based prospecting tool for identifying new parking locations',
                    'Prospect database with status pipeline and contact management',
                    'Prospect scrape jobs and search term configuration',
                    'Prospect interaction logging and outreach tracking',
                ],
            },
            {
                title: 'Platform Infrastructure',
                items: [
                    'Firebase + Angular monorepo with shared TypeScript model package',
                    'Cloud Functions for backend logic, crons, and integrations',
                    'Firestore database with security rules and indexes',
                    'Firebase Storage for images with processing pipeline',
                    'Cloud Build CI/CD pipeline and Firebase emulator support',
                    'Sentry error tracking and Firestore backup automation',
                    'Scheduled cron jobs with admin test-routes page for manual triggering',
                ],
            },
        ],
    },
]

@Component({
    selector: 'app-admin-changelog',
    templateUrl: './admin-changelog.component.html',
    styleUrls: ['./admin-changelog.component.scss'],
})
export class AdminChangelogComponent {
    currentVersion = CURRENT_VERSION
    changelog = CHANGELOG
    expandedVersions: Set<string> = new Set([CURRENT_VERSION])

    toggleVersion(version: string) {
        if (this.expandedVersions.has(version)) {
            this.expandedVersions.delete(version)
        } else {
            this.expandedVersions.add(version)
        }
    }

    isExpanded(version: string): boolean {
        return this.expandedVersions.has(version)
    }

    getSectionIcon(title: string): string {
        const icons: { [key: string]: string } = {
            'Public Website': 'fa-solid fa-globe',
            'Customer App': 'fa-solid fa-user',
            'Merchant Portal': 'fa-solid fa-store',
            'Admin CRM': 'fa-solid fa-address-book',
            'Billing & Invoicing': 'fa-solid fa-file-invoice-dollar',
            'Access Control': 'fa-solid fa-shield-halved',
            'Admin Dashboard & Operations': 'fa-solid fa-gauge-high',
            'Notifications & Communications': 'fa-solid fa-bell',
            'Enterprise & White-Label': 'fa-solid fa-building-columns',
            'Prospecting': 'fa-solid fa-magnifying-glass-location',
            'Platform Infrastructure': 'fa-solid fa-server',
        }
        return icons[title] || 'fa-solid fa-circle'
    }
}
